import { Injectable } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LOG_LEVEL } from '../../enums/log-level.enum';
import { LogInterface } from '../../interfaces/log.interface';
import { LogDexieService } from './log-dexie.service';
import { safeStringify } from '../../../../../pace-components/src/lib/utils/safe-stringify.util';
import { PaceInsightsService } from '../pace-insights/pace-insights.service';
import { logLevelToSeverityLevel } from '../../utils/error.util';

@Injectable()
export class LogIndexedDbService implements LogInterface {
    public constructor(
        private readonly dexieLogger: LogDexieService,
        private readonly loggerService: NGXLogger,
        private readonly paceInsights: PaceInsightsService,
    ) {}

    /**
     *
     * Checks if there are any entries using legacy encryption format.
     *
     * This method queries the collection of stored entries and determines whether
     * any entry's content starts with the legacy encryption prefix.
     *
     * This method is only relevant for the transition period from GW encryption
     * keys to Azure encryption keys. Once, all users that have on encryption
     * key on GW also have an encryption key on Azure, it can safely be removed.
     * ~ summer 2026 should be safe
     *
     * @returns {Observable<boolean>} An observable that emits `true` if at least one
     * entry uses legacy encryption, otherwise `false`.
     */
    public hasLegacyEncryption(): Observable<boolean> {
        return this.dexieLogger.hasLegacyEncryption();
    }

    /**
     * Upgrades all entries from legacy encryption to the current encryption format.
     *
     * This method performs a read-write transaction on the collection of stored entries.
     * It finds all entries whose content starts with the legacy prefix,
     * re-encrypts their content using the `encryptionMigrator`, and updates the entries
     * in the database.
     *
     * This method is only relevant for the transition period from GW encryption
     * keys to Azure encryption keys. Once, all users that have on encryption
     * key on GW also have an encryption key on Azure, it can safely be removed.
     * ~ summer 2026 should be safe
     *
     * @returns {Observable<boolean>} An observable that emits `true` once all legacy
     * encrypted entries have been successfully migrated.
     */
    public upgradeEncryption(): Observable<boolean> {
        return this.dexieLogger.upgradeEncryption();
    }

    public prefix(prefix: string): LogInterface {
        this.warn(`Calls to log.prefix are no longer supported! (Desired prefix was: ${prefix})`);
        return this;
    }

    private convertProperties(additional?: unknown[]): { [p: string]: unknown } | undefined {
        if (!additional || additional?.length < 1) {
            return undefined;
        }
        if (additional?.length === 1 && !!additional[0] && typeof additional[0] === 'object') {
            const entries = Object.entries(additional[0] as Record<string, unknown>);
            return Object.fromEntries(entries.map(([k, v]) => [`${k}`, safeStringify(v)]));
        }
        return Object.fromEntries(additional.map((e, i) => [i, safeStringify(e)]));
    }

    private trackLog(level: LOG_LEVEL, message: string, additional: unknown[]): void {
        const severityLevel = logLevelToSeverityLevel(level);
        if (level === LOG_LEVEL.ERROR) {
            this.paceInsights.trackException({ exception: new Error(message) }, this.convertProperties(additional));
        } else {
            this.paceInsights.trackTrace({ message, severityLevel }, this.convertProperties(additional));
        }
        this.dexieLogger
            .writeEntry(
                LogIndexedDbService.mergeMessageAndAdditionalInfoToString(message, ...additional.map(safeStringify)),
                level,
            )
            .catch((error) => {
                this.loggerService.error(`Could not log message [${level}/${severityLevel}] to IndexedDb: ${error}`);
            });
    }

    public debug(message: string, ...additional: unknown[]): void {
        this.trackLog(LOG_LEVEL.DEBUG, message, additional);
        this.loggerService.debug(message, ...additional);
    }

    public info(message: string, ...additional: unknown[]): void {
        this.trackLog(LOG_LEVEL.INFO, message, additional);
        this.loggerService.info(message, ...additional);
    }

    public log(message: string, ...additional: unknown[]): void {
        this.trackLog(LOG_LEVEL.NONE, message, additional);
        this.loggerService.log(message, ...additional);
    }

    public warn(message: string, ...additional: unknown[]): void {
        this.trackLog(LOG_LEVEL.WARN, message, additional);
        this.loggerService.warn(message, ...additional);
    }

    public error(message: string, ...additional: unknown[]): void {
        this.trackLog(LOG_LEVEL.ERROR, message, additional);
        this.loggerService.error(message, ...additional);
    }

    public getLogs(maxLines?: number): Observable<string> {
        return from(this.dexieLogger.getAllLogEntries(maxLines)).pipe(
            map((logEntries) => {
                const textEntries = logEntries.map((logEntry) => logEntry.toString());
                return textEntries.join('\n');
            }),
        );
    }

    private static mergeMessageAndAdditionalInfoToString(message: string, ...additional: string[]): string {
        if (!additional) {
            additional = [];
        }
        additional.splice(0, 0, message);
        return additional.join(' ');
    }
}
